import i18next from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';

// Discover /locales/{lang}/{ns}.json at build-time, load at runtime
const localeFiles = import.meta.glob('../../locales/*/*.json');

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'de'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const DEFAULT_LANGUAGE: SupportedLanguage = 'fr';
// Preload shared namespaces that are used app-wide
const DEFAULT_NAMESPACES = ['common', 'auth', 'ui', 'fields'] as const;
export const FEATURE_NAMESPACES = [
  'fields',
  'auth',
  'analytics',
  'billing',
  'patients',
  'patient-portal',
  'scheduling',
  'dental-charting',
  'team',
  'treatments',
  'recalls',
  'clinical-records',
  'organisation',
  'practice',
  'rbac',
  'user',
  'online-bookings',
  'settings',
  'message-templates',
  'appointment-analytics',
  'finance'
] as const;

async function loadNamespace(lang: string, ns: string) {
  const path = `../../locales/${lang}/${ns}.json`;
  const loader = localeFiles[path];
  if (!loader) {
    console.warn(`Translation file not found: ${path}`);
    return {};
  }
  try {
    const mod: any = await loader();
    return mod.default || mod;
  } catch (error) {
    console.error(`Failed to load translation file: ${path}`, error);
    return {};
  }
}

export async function ensureNamespaces(
  namespaces: string | string[],
  language: string = i18next.language
): Promise<void> {
  const list = Array.isArray(namespaces) ? namespaces : [namespaces];
  await Promise.all(
    list.map(async (ns) => {
      if (!i18next.hasResourceBundle(language, ns)) {
        const data = await loadNamespace(language, ns);
        i18next.addResourceBundle(language, ns, data, true, true);
        // Ensure react-i18next knows resources were added
        try { (i18next as any).emit?.('added', language, ns); } catch {}
      }
    })
  );
}

export async function initI18n(initialLanguage?: SupportedLanguage): Promise<void> {
  await i18next
    .use(ICU)
    .use(initReactI18next)
    .init({
      lng: initialLanguage || DEFAULT_LANGUAGE,
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
      ns: DEFAULT_NAMESPACES as unknown as string[],
      defaultNS: 'common',
      compatibilityJSON: 'v4', // Explicit modern CLDR pluralization
      interpolation: { 
        escapeValue: false // React already escapes
      },
      resources: {},
      // Disable automatic detection since we handle it manually
      detection: {
        order: [],
        caches: []
      }
    });

  const resolvedLanguage = (initialLanguage || DEFAULT_LANGUAGE);
  
  // Preload base namespaces for the resolved language
  await Promise.all(
    DEFAULT_NAMESPACES.map(async (ns) => {
      const data = await loadNamespace(resolvedLanguage, ns);
      i18next.addResourceBundle(resolvedLanguage, ns, data, true, true);
      try { (i18next as any).emit?.('added', resolvedLanguage, ns); } catch {}
    })
  );

  // Lazy-load on language change
  i18next.on('languageChanged', async (newLanguage) => {
    // Load default namespaces for new language if not already loaded
    await Promise.all(
      DEFAULT_NAMESPACES.map(async (ns) => {
        if (!i18next.hasResourceBundle(newLanguage, ns)) {
          const data = await loadNamespace(newLanguage, ns);
          i18next.addResourceBundle(newLanguage, ns, data, true, true);
        }
      })
    );

    // Keep <html lang="..."> in sync
    document.documentElement.lang = newLanguage;
    document.documentElement.dir = 'ltr'; // change if RTL support is needed
  });

  // Handle missing namespaces with lazy loading
  i18next.on('missingKey', async (lngs, namespace) => {
    const lng = Array.isArray(lngs) ? lngs[0] : lngs;
    if (FEATURE_NAMESPACES.includes(namespace as any)) {
      const data = await loadNamespace(lng, namespace);
      i18next.addResourceBundle(lng, namespace, data, true, true);
      try { (i18next as any).emit?.('added', lng, namespace); } catch {}
    }
  });
}

export default i18next;
